<template>
  <DsPageContent class="kh-article">
    <!-- Article Header: Image in the side Hero -->
    <DsHeroWithMedia
      v-if="!isHeroFullWidthImage && article.title && headerImage"
      theme="grey"
      :screen="screen"
      :title="article.title"
      :image="headerImage"
      :eyebrow="headerTag"
      :navigation-data="{ text: labels.backLink, url: parentPageUrl }"
      :links="[]"
      aspect-ratio="3/4"
    >
      <template #description>
        <p
          v-if="article.shortDescription"
          ref="shortDescription"
          class="ds-text-medium ds-margin-bottom-24"
        >
          {{ article.shortDescription }}
        </p>
        <AuthorComponent
          :image="authorData.image"
          :name="authorData.name"
          :author-add-info="authorAddInfo"
          :date="article.dateFormatted"
          :time="$t('minutesAmount', article.minutesToRead)"
        />
      </template>
    </DsHeroWithMedia>

    <!-- Article Header: Full Width Image Hero -->
    <DsSection
      v-if="isHeroFullWidthImage"
      :with-space-top="false"
      theme="grey"
      full-screen
    >
      <div class="ds-container">
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12">
            <DsLinkBack
              :text="labels.backLink"
              :url="parentPageUrl"
              :tracking-data="{ 'component-name': 'DsHeaderWithDivider', text: labels.backLink, title: article.title }"
              class="ds-margin-top-48 ds-margin-bottom-48"
            />
            <DsScreenWrapper
              v-if="headerImage"
              :ready="hydrateReady"
            >
              <DsHeaderWithDivider
                v-if="headerImage"
                :image="headerImage"
                :screen="screen"
                breakpoint="large"
                theme="grey"
                class="kh-header-with-divider"
              />
            </DsScreenWrapper>
          </div>
        </div>
      </div>
      <div
        v-if="article.title"
        class="ds-container ds-margin-top-48 ds-lg-margin-top-0"
      >
        <div class="ds-grid ds-background-color-grey-100">
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2 ds-lg-padding-x-0">
            <DsEyebrow :text="headerTag" />
            <h1 class="ds-heading ds-md-heading-large ds-margin-bottom-12">
              {{ article.title }}
            </h1>
            <p
              v-if="article.shortDescription"
              ref="shortDescription"
              class="ds-text-medium ds-margin-bottom-24"
            >
              {{ article.shortDescription }}
            </p>
            <AuthorComponent
              :image="authorData.image"
              :name="authorData.name"
              :author-add-info="authorAddInfo"
              :date="article.dateFormatted"
              :time="$t('minutesAmount', article.minutesToRead)"
            />
          </div>
        </div>
      </div>
    </DsSection>

    <!-- Page: Horizontal On Page Navigation -->
    <div
      v-if="showOnPageNavigation"
      class="kh-onpage-nav sticky-top ds-width-full"
    >
      <DsOnPageNavigation
        disposition="horizontal"
        :items="onPageNavItems"
        :screen="screen"
        :title="labels.onThisPage"
        :show-numbers="false"
        :update-route="true"
        :initial-active-item="$route.hash"
        portal-target="header"
        use-grid
      />
    </div>

    <!-- Article Content: Legacy -->
    <DsSection
      v-if="isLegacyContent"
      :full-screen="!isScreenLarge"
      :with-space="false"
      class="ds-padding-y-24 ds-md-padding-y-48"
    >
      <div class="ds-grid">
        <!-- Tags -->
        <div
          v-if="sidebarTags.length"
          class="kh-chapters-tags ds-grid ds-padding-top-24 ds-md-padding-top-48"
        >
          <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2">
            <DsTag
              v-for="(tag, index) in sidebarTags"
              :key="`sidebarTags-${index}`"
              :label="tag.name"
              :link="tag.link"
              color="green"
              class="ds-margin-right-8 ds-margin-bottom-8 ds-margin-top-8"
            />
          </div>
        </div>

        <!-- Social Bar -->
        <div class="ds-grid">
          <DsSocialBar
            disposition="horizontal"
            :items="socialItems"
            class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2 ds-padding-y-24 ds-md-padding-y-48"
          />
        </div>

        <!-- Article Content: Legacy html content -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          ref="legacyContent"
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-2 ds-padding-y-24 ds-md-padding-y-48"
          v-html="article.legacyContent"
        />
      </div>
    </DsSection>

    <!-- Not legacy -->
    <template v-else>
      <!-- Article Content: New (Chapters section) -->
      <KnowledgeHubArticleChapters
        v-if="chapters && chapters.length"
        :sidebar-tags="sidebarTags"
        :intro="article.chaptersIntroText"
        :chapters="chapters"
        :labels="labels"
        :page-seo-data="pageSeoData"
      />
    </template>

    <!-- Related Content -->
    <KnowledgeHubRelatedContent
      :related-content="article.relatedContent"
      :see-all-label="labels.seeAllKnowledgeHubArticles"
      :parent-page-url="parentPageUrl"
    />

    <div
      v-if="newsletterFormData && !article.relatedContent"
      class="ds-container"
    >
      <DsDivider theme="light" />
    </div>

    <!-- Form Section -->
    <NewsletterForm
      v-if="newsletterFormData"
      v-bind="newsletterFormData"
      with-text-medium
      one-column-layout
    />
  </DsPageContent>
</template>
<script setup>
import { kebabCase } from 'lodash-es'
import {
  DsDivider,
  DsHeaderWithDivider,
  DsHeroWithMedia,
  DsLinkBack,
  DsOnPageNavigation,
  DsPageContent,
  DsScreenWrapper,
  DsSection,
  DsSocialBar,
  DsTag,
  DsEyebrow
} from 'awds'
import AuthorComponent from '../../components/knowledge-hub/author-component.vue'

const { screen, isScreenLarge } = useScreen()
const { hydrateReady } = useHydrateHook()
const { labels } = useGlobalData()

const props = defineProps({
  article: { type: Object, default: null },
  pageSeoData: { type: Object, default: null },
  sidebarTags: { type: Array, default: () => [] },
  parentPageUrl: { type: String, default: '' },
  socialItems: { type: Array, default: () => [] },
  headerTag: { type: String, default: '' },
  newsletterFormData: { type: Object, default: null }
})

const chapters = computed(() => {
  return props.article?.chapters?.filter(Boolean)
})

const onPageNavItems = computed(() => {
  return chapters.value
    ?.filter((chapter) => chapter?.navigationTitle || chapter?.title)
    ?.map((chapter) => ({
      label: chapter.navigationTitle || chapter.title,
      slug: kebabCase(chapter.navigationTitle || chapter.title)
    }))
})

const showOnPageNavigation = computed(() => {
  return chapters.value?.length > 3 && onPageNavItems?.value.length
})

const isHeroFullWidthImage = computed(() => {
  return props.article?.displayFullWidthImageHero
})

const isLegacyContent = computed(() => {
  return !chapters?.value?.length && props.article?.legacyContent
})

const authorData = computed(() => {
  return props.article?.author || {}
})

const headerImage = computed(() => {
  if (!props.article?.headerMedia) {
    return null
  }
  if (isHeroFullWidthImage.value) {
    // return default image with aspect ratio 16:9
    return props.article?.headerMedia[3]
  }
  return props.article?.headerMedia?.[0]
})

const authorAddInfo = computed(() => {
  if (!props.article.author) {
    return ''
  }
  return [props.article.author.role, props.article.author.companyName].filter(Boolean).join(', ')
})
</script>
<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';
@import 'awds/foundation-styles.scss';

@mixin force-width-screen-only-large {
  @include wds-screen-only-large {
    margin-left: -$column-percentage-width !important;
    margin-right: -$column-percentage-width !important;
    margin-top: $spacing-24 !important;
    width: 100% + 2 * $column-percentage-width !important;
  }

  @include wds-screen-down(large) {
    width: 100%;
  }
}

/** Styles for new content */

/** Make On Page Nav to be always on top of sidebar */
.kh-onpage-nav.sticky-top {
  z-index: 3;
}

/**
* Styles to render legacy content
*/
/* stylelint-disable selector-max-compound-selectors */

$column-percentage-width: 11%;

:deep(.story-content) {
  .story-text-block--paragraph {
    margin-bottom: $spacing-24;

    p {
      strong {
        padding-left: 2px;
        padding-right: 2px;
      }
    }

    a:not(.ds-blog-card) {
      border-radius: $border-radius-8;
      word-break: break-word;

      // TODO - MIGRATE
      @include text-decoration-underline;
      @include button-link-states(inherit, $color-green, true, $color-green-600);
    }
  }

  .story-text-block--header-2 {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    line-height: $line-height-large;
    margin-bottom: $spacing-24;
    margin-top: $spacing-48;
  }

  .story-text-block--header-3 {
    font-size: $font-size-medium;
    font-weight: $font-weight-bold;
    line-height: $line-height-medium;
    margin-bottom: $spacing-24;
    margin-top: $spacing-24;
  }

  .story-text-image {
    .story-text-image__image {
      /** expand image 1 columns (aprox) */
      @include force-width-screen-only-large();

      margin-bottom: $spacing-24;
    }

    .fw-semi-bold {
      font-weight: $font-weight-semi-bold;
    }

    figure + .area--margin-top-xxsmall {
      margin-bottom: $spacing-48;
      margin-left: -$column-percentage-width;
    }
  }

  .story__dot-prefixed::before {
    content: '\00B7';
    margin: 0 6px;
  }

  .story-quote__content {
    @include force-width-screen-only-large();

    margin: $spacing-48 $spacing-0;

    .story-text__blockquote {
      font-size: $font-size-large;
      line-height: $line-height-large;
      margin-bottom: $spacing-24;
      text-align: center;
    }

    .story-quote__author {
      text-align: center;

      cite {
        font-style: normal;
      }

      .fw-semi-bold {
        font-weight: $font-weight-bold;
      }
    }
  }

  .story-cta {
    margin-bottom: $spacing-24;
    overflow: hidden;

    .story-cta__related-story > p {
      display: none;
    }

    .row {
      display: flex;
      flex-wrap: wrap;

      .story-cta__image {
        flex: 0 0 33.33333%;
        max-width: 33.33333%;
        overflow: hidden;

        .story-cta__logo {
          background-size: contain;
          height: calc(100% - #{$spacing-24});
          margin: $spacing-12;
        }
      }

      .story-cta__content {
        background-color: $color-grey-100;
        flex-basis: 0;
        flex-grow: 1;
        max-width: 100%;
        padding: $spacing-48;

        @include wds-screen-down(large) {
          padding: $spacing-24;
        }

        h3 {
          font-size: $font-size-medium;
          font-weight: $font-weight-semi-bold;
          line-height: $line-height-medium;
          margin-bottom: $spacing-12;
          margin-top: $spacing-12;
        }

        .area--margin-bottom-small {
          margin-bottom: $spacing-12;
        }

        .story-category-name {
          background: $color-green-200;
          border-radius: $border-radius-4;
          color: $color-grey-2500;
          display: inline-block;
          font-size: $font-size-small;
          font-weight: $font-weight;
          height: $line-height-medium;
          line-height: $line-height-small;
          padding: $spacing-8 $spacing-12;
          text-decoration: none;
          white-space: nowrap;
          width: fit-content;
        }

        .story-text__small {
          color: $color-grey-1900;
          font-size: $font-size-small;
          font-weight: $font-weight;
          line-height: $line-height-small;
        }

        .btn {
          margin-top: $spacing-12;

          //@include button;
          //@include button--primary--black;
        }
      }

      @include wds-screen-down('medium') {
        .story-cta__image {
          flex: unset;
          max-width: unset;
          width: 100%;

          .story-cta__logo {
            min-height: 100px;
          }
        }
      }
    }
  }

  .story-list {
    list-style: disc;
    margin-left: $spacing-24;
    margin-top: $spacing-12;
    padding: 0;

    ul {
      list-style: circle;
    }

    li {
      margin-top: $spacing-8;
    }
  }

  .table {
    border-collapse: collapse;
    margin-bottom: $spacing-48;
    margin-top: $spacing-48;
    width: 100%;

    th {
      font-weight: $font-weight-bold;
      text-align: left;
    }

    td,
    th {
      border-bottom: $border-width-1 $border-style $color-grey-500;
      padding: $spacing-24 $spacing-12 $spacing-24 0;
      vertical-align: top;
    }

    tr:last-child {
      td,
      th {
        border-bottom: 0;
      }
    }
  }

  .code-snippet__lines,
  .code-snippet__code {
    font-family: $font-family-for-code;
    font-size: $font-size-small;
    line-height: $line-height-small;
    white-space: pre-wrap;
  }

  .code-snippet__lines {
    background: $color-grey-3200;
    color: $color-grey-1300;
    display: inline-block;
    left: 1px;
    padding: 0 $spacing-24 0 $spacing-24;
    position: absolute;
    top: 25px;

    @include wds-screen('medium') {
      padding: $spacing-24 $spacing-48 0 $spacing-48;
    }
  }

  /** @TODO: improve code snippet */
  .position-relative {
    position: relative;
  }

  .form-wrapper {
    min-height: 520px;
    position: relative;
  }

  .code-snippet__code {
    background: $color-grey-3200;
    border: $border-width-1 $border-style;
    color: $color-white;
    overflow: auto;
    padding: $spacing-24 $spacing-24 $spacing-24 64px;

    @include wds-screen('medium') {
      padding: $spacing-48 $spacing-48 $spacing-48 112px;
    }

    code {
      background: unset;
      margin-bottom: -36px;
      margin-top: -20px;
      padding: unset;
    }
  }

  /** @TODO: remove this divs from the blog import */
  .end-of-blog-divider,
  .row hr {
    display: none;
  }
}

/* stylelint-enable selector-max-compound-selectors */

:deep() .ds-dropdown {
  @include screen('medium') {
    padding-left: $spacing-12;
  }
}
</style>
